import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Card } from '@/components/Card';
import { post } from '@/lib/api';

export function JobCreatePage() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    department_name: '',
    job_type: 'full_time',
    location: '',
    remote_eligible: false,
    hiring_manager_name: '',
    salary_min: '',
    salary_max: '',
    requirements: '',
    responsibilities: '',
    nice_to_haves: '',
    tags: '',
  });

  function update(field: string, value: string | boolean) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      setError('Title and description are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        title: form.title.trim(),
        description: form.description.trim(),
        department_name: form.department_name || undefined,
        job_type: form.job_type,
        location: form.location || undefined,
        remote_eligible: form.remote_eligible,
        hiring_manager_name: form.hiring_manager_name || undefined,
        requirements: form.requirements ? form.requirements.split('\n').map(s => s.trim()).filter(Boolean) : [],
        responsibilities: form.responsibilities ? form.responsibilities.split('\n').map(s => s.trim()).filter(Boolean) : [],
        nice_to_haves: form.nice_to_haves ? form.nice_to_haves.split('\n').map(s => s.trim()).filter(Boolean) : [],
        tags: form.tags ? form.tags.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      if (form.salary_min && form.salary_max) {
        payload.salary_range = {
          min: parseFloat(form.salary_min),
          max: parseFloat(form.salary_max),
          currency: 'USD',
        };
      }
      const result = await post<{ id: string }>('jobs', payload);
      navigate(`/jobs/${result.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create job');
    } finally {
      setSaving(false);
    }
  }

  const inputClass = 'w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-amber-500';
  const labelClass = 'text-xs font-medium text-muted-foreground block mb-1';

  return (
    <div className="space-y-6 max-w-3xl">
      <button onClick={() => navigate('/jobs')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> Back to Jobs
      </button>

      <h2 className="text-xl font-bold text-foreground">Create Job Posting</h2>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-4">Job Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className={labelClass}>Job Title *</label>
              <input value={form.title} onChange={e => update('title', e.target.value)} placeholder="Senior Systems Engineer" className={inputClass} required />
            </div>
            <div>
              <label className={labelClass}>Department</label>
              <input value={form.department_name} onChange={e => update('department_name', e.target.value)} placeholder="Engineering" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Job Type</label>
              <select value={form.job_type} onChange={e => update('job_type', e.target.value)} className={inputClass}>
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
                <option value="contract">Contract</option>
                <option value="intern">Intern</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Location</label>
              <input value={form.location} onChange={e => update('location', e.target.value)} placeholder="New York, NY" className={inputClass} />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <input type="checkbox" id="remote_eligible" checked={form.remote_eligible} onChange={e => update('remote_eligible', e.target.checked)} className="rounded border-border" />
              <label htmlFor="remote_eligible" className="text-sm text-foreground">Remote Eligible</label>
            </div>
            <div>
              <label className={labelClass}>Hiring Manager</label>
              <input value={form.hiring_manager_name} onChange={e => update('hiring_manager_name', e.target.value)} placeholder="Jane Doe" className={inputClass} />
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-4">Salary Range</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Minimum (USD)</label>
              <input type="number" value={form.salary_min} onChange={e => update('salary_min', e.target.value)} placeholder="60000" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Maximum (USD)</label>
              <input type="number" value={form.salary_max} onChange={e => update('salary_max', e.target.value)} placeholder="90000" className={inputClass} />
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-4">Description & Requirements</h3>
          <div className="space-y-4">
            <div>
              <label className={labelClass}>Description *</label>
              <textarea value={form.description} onChange={e => update('description', e.target.value)} placeholder="Describe the role, team, and what makes this opportunity exciting..." rows={5} className={inputClass} required />
            </div>
            <div>
              <label className={labelClass}>Requirements (one per line)</label>
              <textarea value={form.requirements} onChange={e => update('requirements', e.target.value)} placeholder="5+ years experience with cloud infrastructure&#10;Strong understanding of networking&#10;Relevant certifications (Azure, AWS)" rows={4} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Responsibilities (one per line)</label>
              <textarea value={form.responsibilities} onChange={e => update('responsibilities', e.target.value)} placeholder="Design and maintain cloud infrastructure&#10;Mentor junior engineers&#10;Participate in on-call rotation" rows={4} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Nice to Have (one per line)</label>
              <textarea value={form.nice_to_haves} onChange={e => update('nice_to_haves', e.target.value)} placeholder="Kubernetes experience&#10;Terraform certification&#10;MSP background" rows={3} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Tags (comma-separated)</label>
              <input value={form.tags} onChange={e => update('tags', e.target.value)} placeholder="engineering, senior, cloud" className={inputClass} />
            </div>
          </div>
        </Card>

        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={() => navigate('/jobs')} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-surface-2">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="flex items-center gap-2 px-6 py-2 bg-amber-500 text-foreground rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 transition-colors">
            <Save className="w-4 h-4" /> {saving ? 'Creating...' : 'Create Job'}
          </button>
        </div>
      </form>
    </div>
  );
}
